/**
 * QueryProvider — TanStack Query setup for the entire application.
 *
 * WHY THIS EXISTS:
 * TanStack Query needs a QueryClientProvider at the root of the app.
 * This component configures global defaults for caching, retries,
 * and error handling.
 *
 * INTEGRATION:
 * Add this to the root layout:
 *   <QueryProvider>{children}</QueryProvider>
 */

'use client';

import { useState, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiError } from '@/contracts';

function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // ─── Caching ────────────────────────────────────────────────────
        // Stale time: 30s — data is fresh for 30s before background refetch
        staleTime: 30_000,

        // Garbage collection: 5min — cached data kept for 5min after last use
        gcTime: 5 * 60 * 1000,

        // ─── Refetching (Stale-While-Revalidate) ───────────────────────
        // Refetch on mount and reconnect for freshness, but NOT on window
        // focus. Window-focus refetch causes visible loading churn every time
        // a user switches tabs — the query fires, isPending flips true, and
        // components flash skeletons or spinners for data that was fresh 2s
        // ago. This is the single largest source of perceived UI instability.
        //
        // Hooks that genuinely need focus-refetch (presigned URLs with short
        // TTLs, billing state, security sessions) opt in explicitly with
        // refetchOnWindowFocus: true. Everything else relies on staleTime +
        // SSE-driven invalidation for freshness.
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,

        // ─── Retry Logic ────────────────────────────────────────────────
        // Only retry on 5xx server errors, not 4xx client errors
        retry: (failureCount, error) => {
          if (error instanceof ApiError && error.status < 500) return false;
          return failureCount < 2;
        },

        // ─── Network Mode ───────────────────────────────────────────────
        // Fail immediately if offline (don't queue requests)
        networkMode: 'online',
      },
      mutations: {
        // Mutations never retry (they're not idempotent by default)
        retry: false,

        // Mutations fail immediately if offline
        networkMode: 'online',
      },
    },
  });
}

// Singleton for SSR — prevents creating a new client per request
let browserQueryClient: QueryClient | undefined;

export function getQueryClient(): QueryClient {
  if (typeof window === 'undefined') {
    // SSR: always create a new client
    return makeQueryClient();
  }
  // Browser: reuse the same client
  if (!browserQueryClient) {
    browserQueryClient = makeQueryClient();
  }
  return browserQueryClient;
}

interface QueryProviderProps {
  children: ReactNode;
}

export function QueryProvider({ children }: QueryProviderProps) {
  const [queryClient] = useState(() => getQueryClient());

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}
